import { ApiError } from '@/lib/api';
import type {
  InvolvementLevel,
  PersonaPresentationFilter,
  PersonaReferenceError,
} from '@matter_extractor/shared';
import {
  PERSONA_LEVEL_FILTER_ALL,
  PERSONA_LEVEL_FILTER_RETIRED,
  assertNever,
  personaReferenceErrorSchema,
} from '@matter_extractor/shared';

/**
 * The words Réglages > Personas speaks about its two reference lists (§11.5).
 *
 * Same régime as `versioned-labels.ts` and `model-labels.ts`: a refusal is
 * worded from its CODE and never from the server's sentence, which is an
 * English developer string bound for the logs and the OpenAPI. Two spellings of
 * one refusal drift, and the half that drifts is the one nobody reads.
 *
 * Its own module rather than a branch of `versioned-labels.ts`, because these
 * two lists are NOT versioned: `draft-stale`, `no-change`, `pointer-empty` and
 * `version-unknown` mean nothing here, and folding two catalogues into one
 * would offer six codes where two exist.
 */

/**
 * The refusal inside a failed write, or `null` when there was none.
 *
 * `ApiError.details` is kept unparsed by `lib/api.ts`, so the parse happens
 * here, once. A body that is not one of the two codes reads as « no typed
 * refusal » and the caller falls back to its own generic sentence, which is the
 * honest answer for a 500.
 */
export function personaReferenceRefusalOf(error: unknown): PersonaReferenceError | null {
  if (!(error instanceof ApiError)) return null;
  const parsed = personaReferenceErrorSchema.safeParse(error.details);
  return parsed.success ? parsed.data : null;
}

/**
 * Whether a finished write SETTLED ON A STATE OF THE BASE - which is what
 * decides, in `reglages.personas.tsx`, whether the section comes back to what
 * the base holds or keeps the rows the operator typed.
 *
 * Two outcomes settle: it landed, or the server refused it with one of the two
 * codes of §11.5. A refusal IS a state of the base - a name a colleague took,
 * an échelle that would drop under two crans - so the honest next screen is the
 * base, and the block composed against a list that has moved is not.
 *
 * Everything else - a dropped connection, a 500, a 400 - settles NOTHING. The
 * base is where it was, the operator's edits are still the only place they
 * exist, and throwing them away to re-render the same list is a punishment for
 * a failure that was not theirs. The message stays under the section and the
 * button can be pressed again.
 *
 * A boolean and not a branch in the route, because `vitest.config.ts` leaves
 * `src/routes/**` out of the coverage report and a rule nothing measures is a
 * rule that rots. Beside `personaReferenceRefusalOf` because it is that same
 * question asked once more.
 */
export function writeSettledOnTheBase(error: unknown): boolean {
  if (error === null || error === undefined) return true;
  return personaReferenceRefusalOf(error) !== null;
}

/**
 * A failed write of the scale or of the library, in French.
 *
 * `name-taken` NAMES the row that holds the name (H16, §16): « déjà pris »
 * sends an operator hunting through a list, « le ton « chaleureux » porte déjà
 * ce nom » does not. `minimum-levels` says the RULE rather than the state,
 * because the operator's next move depends on it.
 *
 * `null` when the failure carried no typed refusal - the caller then shows its
 * own generic sentence rather than an empty box.
 */
export function personaReferenceMessage(error: unknown): string | null {
  const refusal = personaReferenceRefusalOf(error);
  if (refusal === null) return null;
  switch (refusal.code) {
    case 'name-taken':
      return refusal.name === undefined
        ? 'Ce nom est déjà pris. Choisissez-en un autre.'
        : `« ${refusal.name} » porte déjà ce nom. Choisissez-en un autre.`;
    case 'minimum-levels':
      return 'L’échelle garde au moins deux crans : un slider à une seule position ne règle rien.';
    default:
      return assertNever(refusal.code, 'personaReferenceRefusal');
  }
}

/**
 * What the deletion confirmation of a cran SAYS about the personas it touches
 * (§11.5): the count, and that none of them is invalidated.
 *
 * A whole SENTENCE and not a count the caller glues a verb to, and that is the
 * lesson of the first capture of this screen: « aucun persona » beside a
 * `count === 1 ? 'pointe' : 'pointent'` renders « aucun persona pointent ce
 * cran », because zero takes the singular in French and the ternary only knew
 * about one. French agreement is not a plural `s`, so the three readings are
 * written out here, together, where they can be read side by side.
 *
 * Written by hand rather than with `Intl.PluralRules` for the reason the two
 * dates of `versioned-labels.ts` are: `Intl` answers according to the machine's
 * locale, so a test and a capture would not say the same thing on two runners.
 */
export function levelDeletionSentence(count: number): string {
  if (count === 0) return 'Aucun persona ne pointe ce cran en développement.';
  const who = count === 1 ? '1 persona pointe ce cran' : `${count} personas pointent ce cran`;
  return `${who} en développement. Aucun n’est invalidé : chacun garde son nom, son texte et une note.`;
}

/* ---------------------------------- the words of the library and its editor */

/**
 * The words the Personas LIST and EDITOR speak, and that belong to no other
 * object (refonte modulaire §10, §15).
 *
 * Below the line because they arrived with the page (issue #42) while what is
 * above arrived with the two reference lists (issue #41). One labels module per
 * object is the régime `criterion-labels.ts` sets, and a second file would have
 * put the tone's refusal and the tone's tag two imports apart.
 */

/** The persona's first own filter (§15). « Présentation » is feminine: « Toutes ». */
export const PRESENTATION_FILTER_LABEL: Readonly<Record<PersonaPresentationFilter, string>> = {
  all: 'Toutes',
  anonymous: 'Anonyme',
  named: 'Nommée',
};

/** The option that finds the personas whose cran has left the scale (§15). */
export const RETIRED_LEVEL_LABEL = 'Cran retiré';

/** The neutral option of the cran filter. « Cran » is masculine: « Tous ». */
export const ALL_LEVELS_LABEL = 'Tous';

/**
 * How one value of the cran filter is named - in the select, and in the chip
 * that removes it.
 *
 * The two reserved words, then the scale. An identifier matching NOTHING is the
 * one degenerate case, and it is reachable: a link pasted after somebody deleted
 * that cran in Réglages. It is named as unknown rather than silently relabelled
 * « Cran retiré », which is a different filter and would be a lie - the grid
 * under it is empty either way, and only the honest label says why.
 */
export function levelFilterLabel(value: string, levels: readonly InvolvementLevel[]): string {
  if (value === PERSONA_LEVEL_FILTER_ALL) return ALL_LEVELS_LABEL;
  if (value === PERSONA_LEVEL_FILTER_RETIRED) return RETIRED_LEVEL_LABEL;
  return levels.find((level) => level.id === value)?.name ?? 'cran inconnu';
}

/**
 * The SUBTITLE under a card's name: « Claire · ton chaleureux », « Anonyme ·
 * sans ton » (§15, maquette `Carte`).
 *
 * Who speaks, then how. Both halves ALWAYS render - an anonymous persona is
 * « Anonyme », a persona without a tone is « sans ton » - because the subtitle
 * answers one question and half an answer reads as a missing word rather than
 * as an absence. That is the opposite arbitrage from the usage line below it,
 * and deliberately so: a usage of zero says nothing, « sans ton » says
 * something.
 */
export function personaSubtitle(firstName: string | null, toneName: string | null): string {
  const who = firstName ?? 'Anonyme';
  return `${who} · ${toneName === null ? 'sans ton' : `ton ${toneName}`}`;
}

/**
 * The letter inside a named persona's avatar: the FIRST NAME's initial (§15).
 *
 * The first name and never the operator's name, which is the whole point of
 * having two (§10.1): the avatar shows who the person is talking to.
 *
 * Spread into code points rather than indexed, because `"Élodie"[0]` is a whole
 * character here but `"👩‍🚀"[0]` is half a surrogate pair - and a name is free
 * text. Uppercased WITHOUT a locale, because `toLocaleUpperCase` reads the
 * machine's and a capture would then photograph the runner's dotless Turkish i.
 */
export function personaInitial(firstName: string): string {
  return [...firstName.trim()][0]?.toUpperCase() ?? '';
}
